import { useState, useRef, useEffect } from "react";
import { format, parse } from "date-fns";
import { es } from "date-fns/locale";
import { Calendar as CalendarIcon, X as XIcon } from "@phosphor-icons/react";

/**
 * DatePicker inline (sin Portal ni Radix Popover) — funciona en iOS Safari/Chrome
 * incluso cuando está dentro de un Dialog. Renderiza el selector como un panel
 * que aparece debajo del trigger, dentro del mismo árbol DOM.
 *
 * Trabaja siempre con string YYYY-MM-DD interpretado en zona LOCAL (sin UTC).
 * Sanea años en formato 4 dígitos con relleno de ceros (0026 → 2026).
 */
export function DatePicker({ value, onChange, placeholder = "Elige fecha", className = "", testId = "date-picker-trigger", fromYear, toYear }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Normaliza el valor entrante: acepta ISO con hora, corrige años 0026 → 2026.
  const cleanValue = (() => {
    if (!value || typeof value !== "string") return "";
    const s = value.slice(0, 10);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return "";
    let y = parseInt(m[1], 10);
    if (y < 100) y = 2000 + y;
    if (y < 1900 || y > 2100) return "";
    return `${String(y).padStart(4, "0")}-${m[2]}-${m[3]}`;
  })();

  const dateObj = cleanValue ? parse(cleanValue, "yyyy-MM-dd", new Date()) : null;
  const displayText = dateObj && !isNaN(dateObj)
    ? format(dateObj, "EEE dd 'de' MMMM 'de' yyyy", { locale: es })
    : placeholder;

  const now = new Date();
  const currentYear = now.getFullYear();
  const fY = fromYear || (currentYear - 5);
  const tY = toYear || currentYear + 10;

  // Estado interno del calendario mostrado (mes / año que se está visualizando)
  const [viewMonth, setViewMonth] = useState((dateObj || now).getMonth());
  const [viewYear, setViewYear] = useState((dateObj || now).getFullYear());

  // Sincronizar viewMonth/viewYear al abrir con la fecha seleccionada
  useEffect(() => {
    if (open) {
      const base = dateObj || now;
      setViewMonth(base.getMonth());
      setViewYear(base.getFullYear());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Cerrar al hacer click fuera
  useEffect(() => {
    if (!open) return;
    const onOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("touchstart", onOutside);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("touchstart", onOutside);
    };
  }, [open]);

  // Cerrar con Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const months = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
  const years = [];
  for (let y = fY; y <= tY; y++) years.push(y);

  // Grid del mes
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstWeekday = (new Date(viewYear, viewMonth, 1).getDay() + 6) % 7; // Lunes = 0
  const dayCells = [];
  for (let i = 0; i < firstWeekday; i++) dayCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) dayCells.push(d);

  const selectDay = (d) => {
    if (!d) return;
    const dd = String(d).padStart(2, "0");
    const mm = String(viewMonth + 1).padStart(2, "0");
    const yyyy = String(viewYear).padStart(4, "0");
    onChange(`${yyyy}-${mm}-${dd}`);
    setOpen(false);
  };

  const isSelected = (d) => (
    dateObj &&
    dateObj.getFullYear() === viewYear &&
    dateObj.getMonth() === viewMonth &&
    dateObj.getDate() === d
  );
  const isToday = (d) => (
    now.getFullYear() === viewYear &&
    now.getMonth() === viewMonth &&
    now.getDate() === d
  );

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      {/* Trigger — usa onPointerDown en iOS para evitar delay de 300ms y garantizar respuesta al touch */}
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(o => !o); }}
        onTouchEnd={(e) => { e.preventDefault(); setOpen(o => !o); }}
        style={{ touchAction: "manipulation", WebkitTapHighlightColor: "rgba(255,77,0,0.2)" }}
        className={`w-full h-10 px-3 flex items-center justify-start text-left text-sm border border-white/10 bg-[#1A1A1A] hover:border-[#FF4D00]/40 active:border-[#FF4D00] cursor-pointer ${cleanValue ? "text-white" : "text-gray-500"}`}
        data-testid={testId}
      >
        <CalendarIcon size={16} className="mr-2 text-[#FF4D00] flex-shrink-0" />
        <span className="capitalize truncate">{displayText}</span>
      </button>

      {/* Panel — inline en desktop, fullscreen bottom sheet en móvil */}
      {open && (
        <>
          {/* Backdrop en móvil */}
          <div
            className="fixed inset-0 bg-black/60 z-[9998] md:hidden"
            onClick={() => setOpen(false)}
            onTouchEnd={(e) => { e.preventDefault(); setOpen(false); }}
            aria-hidden="true"
          />
          <div
            className="fixed inset-x-0 bottom-0 md:absolute md:left-0 md:inset-x-auto md:bottom-auto md:top-full md:mt-1 md:w-[300px] bg-[#0F0F0F] border border-white/10 shadow-2xl p-3 z-[9999] max-h-[85vh] overflow-y-auto rounded-t-2xl md:rounded-none"
            data-testid="date-picker-panel"
            onClick={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
          >
          {/* Selectors Mes / Año */}
          <div className="flex gap-2 mb-3">
            <select
              value={viewMonth}
              onChange={(e) => setViewMonth(parseInt(e.target.value, 10))}
              className="flex-1 h-9 bg-[#1A1A1A] border border-white/10 text-white text-sm px-2 focus:outline-none focus:border-[#FF4D00]"
              data-testid="date-picker-month-select"
            >
              {months.map((m, i) => (
                <option key={i} value={i} className="bg-[#1A1A1A]">{m.charAt(0).toUpperCase() + m.slice(1)}</option>
              ))}
            </select>
            <select
              value={viewYear}
              onChange={(e) => setViewYear(parseInt(e.target.value, 10))}
              className="w-24 h-9 bg-[#1A1A1A] border border-white/10 text-white text-sm px-2 focus:outline-none focus:border-[#FF4D00]"
              data-testid="date-picker-year-select"
            >
              {years.map(y => (
                <option key={y} value={y} className="bg-[#1A1A1A]">{y}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-white border border-white/10"
              aria-label="Cerrar"
            >
              <XIcon size={14} />
            </button>
          </div>

          {/* Encabezado días semana */}
          <div className="grid grid-cols-7 gap-1 mb-1 text-[10px] uppercase tracking-wider text-gray-500 text-center font-bold">
            {["Lu", "Ma", "Mi", "Ju", "Vi", "Sá", "Do"].map(d => <div key={d}>{d}</div>)}
          </div>

          {/* Grid de días */}
          <div className="grid grid-cols-7 gap-1">
            {dayCells.map((d, i) => {
              if (d === null) return <div key={`e${i}`} />;
              const sel = isSelected(d);
              const today = isToday(d);
              return (
                <button
                  key={d}
                  type="button"
                  onClick={(e) => { e.preventDefault(); selectDay(d); }}
                  onTouchEnd={(e) => { e.preventDefault(); selectDay(d); }}
                  style={{ touchAction: "manipulation" }}
                  className={`h-10 text-sm font-medium border transition-colors ${
                    sel
                      ? "bg-[#FF4D00] border-[#FF4D00] text-white"
                      : today
                        ? "bg-[#1A1A1A] border-[#FF4D00]/50 text-white"
                        : "bg-transparent border-transparent text-gray-300 hover:bg-white/5 hover:border-white/10 active:bg-[#FF4D00]/30"
                  }`}
                  data-testid={`date-picker-day-${d}`}
                >
                  {d}
                </button>
              );
            })}
          </div>
        </div>
        </>
      )}
    </div>
  );
}

export default DatePicker;
